/**
 * Where each "Needs attention" row opens — Card P170.
 *
 * ⛔⛔ THE DEFECT THIS REPLACES. `openException` matched two rule keys inline
 * and fell through to CRM ▸ My Day for everything else. Measured live on
 * v2.1.24 (30 Oct ~02:20 UTC): "124 unpaid invoices (due on demand) ▸ View →"
 * landed on My Day, unfiltered, with nothing on screen saying the records were
 * not the ones counted. THREE of the five rules were in that state, not one —
 * `unpaid_invoices`, `uncontacted_leads` and `consult_no_shows` — because the
 * fallback is silent by construction: adding a rule to routes/_ceo_attention.js
 * needs no change here, so a new rule arrives already broken and nothing says so.
 *
 * ⭐⭐ EVERY RULE KEY MUST APPEAR HERE. Not "every rule key that drills" — every
 * one. A rule may legitimately have no drill target yet, but that has to be a
 * DECLARED decision with a reason, never the default that silence produces.
 * tests/unit/attentionTargets.test.js joins this table to the rule list in
 * routes/_ceo_attention.js and fails on any key missing from either side, so a
 * new rule cannot reach a user before someone has decided where it goes.
 *
 * ⚠ TWO SHAPES, AND `navigate` IS NOT A SOFT FAILURE. A `metric` entry opens
 * the shared pop-up on exactly the records counted. A `navigate` entry goes to
 * the CRM — a DIFFERENT screen, which is honest about being different. What is
 * forbidden is the third thing the old code did: opening something adjacent
 * that looks like the right records and is not.
 *
 * ⭐ AS OF P175 NO RULE USES `navigate`. The shape stays because the next rule
 * added may genuinely have nowhere to drill, and the honest answer then is a
 * declared reason — not a silent fallback. A test asserts the count of
 * reason-only targets is ZERO today, so adding one is a visible decision
 * rather than a quiet regression.
 */

export const ATTENTION_TARGETS = Object.freeze({
  /**
   * High-value deals stuck in a stage.
   * ⚠ The thresholds travel from the ITEM, not from this file's idea of the
   * defaults, so a server-side change moves the drill-down with the row.
   */
  stuck_deals: Object.freeze({
    metric: 'attention.stuck_deals',
    filtersFrom: Object.freeze(['minValue', 'minDaysInStage']),
  }),

  /** Overdue follow-ups, one row per rep. Scoped to that rep — null is Unassigned. */
  overdue_follow_ups: Object.freeze({
    metric: 'rep.overdue_tasks',
    groupBy: 'rep',
    groupFrom: 'assignedTo',
  }),

  /**
   * ⭐ Card P170 — the row this card exists for. Opens the SAME invoices the
   * rule counted, grouped by age since issue, and the row list hands off to
   * invoice detail like every other finance drill-down.
   *
   * ⛔ `finance.unpaid`, NOT `finance.overdue`. Overdue comes from GHL's own
   * due date and is a different set; opening it here would show a plausible
   * list of the wrong invoices under the right heading.
   */
  unpaid_invoices: Object.freeze({
    metric: 'finance.unpaid',
    /*
     * ⚠ NO `dimension` HERE, DELIBERATELY. The resolver defaults to the
     * metric's own FIRST declared dimension (P160 item 3), and UNPAID_DIMENSIONS
     * declares `age` first precisely so this opens on the age split Daniel
     * asked for. Passing it again from the client would be a second place to
     * change the same thing, and MetricExplorer has no prop to receive it —
     * a value nothing reads is a paragraph, not a setting.
     */
    /*
     * ⚠ finance:read, not management. The row list carries invoice detail and
     * a bill-to name. A viewer without it sees the exception COUNT on the
     * dashboard and no "View" affordance — not a button that 403s.
     */
    requires: 'finance:read',
  }),

  /**
   * ⭐ P175 — the last two dead rows. Both opened CRM ▸ My Day until now, and
   * both were declared `navigate` in P170 because no metric existed. These are
   * those metrics.
   *
   * ⚠ The thresholds travel from the ITEM's own link filter, so a drill-down
   * answers the question the row was computed with rather than this file's
   * idea of the defaults.
   */
  uncontacted_leads: Object.freeze({
    metric: 'attention.uncontacted_leads',
    /* ⚠ The ITEM'S OWN KEY NAMES, read straight through. A rename map here
       would be a third vocabulary between the rule, the link and the metric. */
    filtersFrom: Object.freeze(['uncontactedHours', 'withinDays']),
  }),
  consult_no_shows: Object.freeze({
    metric: 'attention.consult_no_shows',
    filtersFrom: Object.freeze(['notRescheduledWithinDays', 'withinDays']),
  }),
});

/** The target for a rule key, or null when the key is unknown to this table. */
export function targetFor(ruleKey) {
  if (typeof ruleKey !== 'string' || !ruleKey) return null;
  return Object.prototype.hasOwnProperty.call(ATTENTION_TARGETS, ruleKey)
    ? ATTENTION_TARGETS[ruleKey]
    : null;
}
